import React, {useCallback, useEffect, useRef, useState} from "react"
import {createPortal} from "react-dom"

import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext"
import {useLexicalEditable} from "@lexical/react/useLexicalEditable"
import {$getNearestNodeFromDOMNode} from "lexical"
import {$getTableNodeFromLexicalNodeOrThrow, $isSimpleTable} from "@lexical/table"
import {
    applyColumnWidth,
    getCellElemFromTarget,
    getColumnIndex,
    isOverRightEdge,
    measureColumnWidths,
} from "./functions"

const MIN_COLUMN_WIDTH = 40

const debug = false
// const debug = "TableResizePlugin"

type Handle = {
    left    : number
    top     : number
    height  : number
    cellElem: HTMLElement
}

type Drag = {
    startX      : number
    startWidth  : number
    index       : number
    measured    : number[]
    cellElem    : HTMLElement
}

type Props = {
    anchorElem?: HTMLElement
}

export default function TableResizePlugin(props: Props) {
    const [editor]          = useLexicalComposerContext()
    const isEditable        = useLexicalEditable()
    const [handle, setHandle]   = useState<null | Handle>(null)
    const [dragX, setDragX]     = useState<null | number>(null)
    const dragRef               = useRef<null | Drag>(null)

    const toLocal = useCallback((clientX: number, clientY: number) => {
        const rect = props.anchorElem?.getBoundingClientRect()

        return {
            x: clientX - (rect?.left || 0),
            y: clientY - (rect?.top || 0),
        }
    }, [props.anchorElem])

    const onPointerMove = useCallback((event: PointerEvent) => {
        // while dragging, only track the ruler
        if (dragRef.current) {
            setDragX(toLocal(event.clientX, 0).x)
            return
        }

        // the handle lives outside the contenteditable, so moving onto it reports a target that is
        // not a cell - without this the handle would remove itself before it could be grabbed
        const target = event.target as HTMLElement
        if (target?.classList?.contains('editor-table-resize-handle')) {
            return
        }

        const cellElem = getCellElemFromTarget(event.target)
        if (!cellElem || !isOverRightEdge(cellElem, event.clientX)) {
            setHandle(null)
            return
        }

        const rect  = cellElem.getBoundingClientRect()
        const local = toLocal(rect.right, rect.top)

        setHandle({
            left    : local.x,
            top     : local.y,
            height  : rect.height,
            cellElem,
        })
    }, [toLocal])

    const endDrag = useCallback(() => {
        const drag = dragRef.current
        if (!drag) {
            return
        }

        dragRef.current = null
        setDragX(null)
        setHandle(null)
    }, [])

    // commit happens on pointerup, but needs the final x - kept in a ref-driven handler
    const finishDrag = useCallback((clientX: number) => {
        const drag = dragRef.current
        if (!drag) {
            return
        }

        const width = Math.max(MIN_COLUMN_WIDTH, drag.startWidth + (clientX - drag.startX))

        debug && console.log(debug, 'finishDrag', drag.index, width)

        editor.update(() => {
            const cellNode = $getNearestNodeFromDOMNode(drag.cellElem)
            if (!cellNode) {
                return
            }
            const tableNode = $getTableNodeFromLexicalNodeOrThrow(cellNode)
            applyColumnWidth(tableNode, drag.index, width, drag.measured)
        })

        endDrag()
    }, [editor, endDrag])

    const onPointerDown = useCallback((event: React.PointerEvent) => {
        if (!handle) {
            return
        }

        const cellElem  = handle.cellElem
        const tableElem = cellElem.closest('table') as HTMLElement | null
        if (!tableElem) {
            return
        }

        let index: number | null = null
        let simple = false
        editor.read(() => {
            const cellNode = $getNearestNodeFromDOMNode(cellElem)
            if (!cellNode) {
                return
            }
            // column index ignores colspans, so only offer resizing on simple grids
            simple  = $isSimpleTable($getTableNodeFromLexicalNodeOrThrow(cellNode))
            index   = getColumnIndex(cellElem)
        })

        if (!simple || index === null) {
            debug && console.log(debug, 'skip resize - not a simple table')
            return
        }

        event.preventDefault()
        event.stopPropagation()

        // capture the pointer so the release is always delivered here - a plain mouseup is not
        // dispatched at all when the button goes up outside the window, which used to leave the
        // drag running and tracking the pointer with no button held
        event.currentTarget.setPointerCapture?.(event.pointerId)

        dragRef.current = {
            startX      : event.clientX,
            startWidth  : cellElem.getBoundingClientRect().width,
            index,
            measured    : measureColumnWidths(tableElem),
            cellElem,
        }
        setDragX(toLocal(event.clientX, 0).x)
    }, [editor, handle, toLocal])

    useEffect(() => {
        if (!isEditable) {
            return
        }

        const root = editor.getRootElement()
        if (!root) {
            return
        }

        const up = (event: PointerEvent) => {
            return finishDrag(event.clientX)
        }

        // pointerdown is bound to the handle element itself (see below) - it sits outside the
        // contenteditable, so a listener on the root would never see it
        root.addEventListener('pointermove', onPointerMove)
        document.addEventListener('pointermove', onPointerMove)
        document.addEventListener('pointerup', up)
        // capture can also be lost without a pointerup - pointercancel, or the handle leaving the
        // DOM. this fires after pointerup too, where finishDrag is already a no-op
        document.addEventListener('lostpointercapture', up)

        return () => {
            root.removeEventListener('pointermove', onPointerMove)
            document.removeEventListener('pointermove', onPointerMove)
            document.removeEventListener('pointerup', up)
            document.removeEventListener('lostpointercapture', up)
        }
    }, [editor, isEditable, onPointerMove, finishDrag])

    if (!isEditable || !props.anchorElem || !handle) {
        return null
    }

    return (
        createPortal(
            <>
                <div className     = {Classes('editor-table-resize-handle', {'is-dragging': dragX !== null})}
                     style         = {{left: handle.left - 4, top: handle.top, height: handle.height}}
                     onPointerDown = {onPointerDown}/>
                {dragX !== null &&
                    <div className  = "editor-table-resize-ruler"
                         style      = {{left: dragX, top: handle.top, height: handle.height}}/>}
            </>,
            props.anchorElem,
        )
    )
}
